import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import Layout from '../components/Layout';
import { intakeFormService } from '../services/intakeFormService';
import { patientService } from '../services/patientService';
import './IntakeForms.css';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const IntakeForms = () => {
  const navigate = useNavigate();
  const [intakeForms, setIntakeForms] = useState([]);
  const [patients, setPatients] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchIntakeForms();
    fetchPatients();
  }, []);

  // Set up WebSocket connection for real-time status updates
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    console.log('🔌 Setting up WebSocket for IntakeForms');

    socket.on('connect', () => {
      console.log('✅ WebSocket connected in IntakeForms');
    });

    // Helper function to refresh forms
    const refreshForms = (reason) => {
      console.log(`🔄 Refreshing IntakeForms - Reason: ${reason}`);
      // Add delay to ensure backend has saved changes
      setTimeout(() => {
        console.log('📡 Fetching fresh forms from server...');
        fetchIntakeForms();
      }, 1000); // Increased delay to 1 second
    };

    // Listen for report status updates
    socket.on('reportStatus', (status) => {
      console.log('📋 Report Status Update received in IntakeForms:', JSON.stringify(status, null, 2));

      // Refresh intake forms for any status change
      if (status.type === 'complete' || status.type === 'generating' || status.type === 'start') {
        console.log(`✅ Report status: ${status.type} - Refreshing forms`);
        refreshForms(`Report status: ${status.type}`);
      }
    });

    // Listen for PDF generation status updates
    socket.on('pdfStatus', (status) => {
      console.log('📄 PDF Status Update received in IntakeForms:', JSON.stringify(status, null, 2));

      // Refresh intake forms for any status change
      if (status.type === 'complete' || status.type === 'start') {
        console.log(`✅ PDF status: ${status.type} - Refreshing forms`);
        refreshForms(`PDF status: ${status.type}`);
      }
    });

    // Listen for generic notifications
    socket.on('notification', (notification) => {
      console.log('🔔 Notification received in IntakeForms:', JSON.stringify(notification, null, 2));

      // Refresh for any success notification
      if (notification.type === 'success' || notification.message?.toLowerCase().includes('complete')) {
        console.log('✅ Success notification - Refreshing forms');
        refreshForms('Success notification');
      }
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 Disconnecting WebSocket in IntakeForms');
      socket.off('connect');
      socket.off('reportStatus');
      socket.off('pdfStatus');
      socket.off('notification');
      socket.disconnect();
    };
  }, []);

  // Add periodic polling as a backup to ensure data freshness
  useEffect(() => {
    console.log('⏰ Setting up periodic forms refresh (every 15 seconds)');
    const pollInterval = setInterval(() => {
      console.log('⏰ Periodic forms refresh triggered');
      fetchIntakeForms();
    }, 15000); // Poll every 15 seconds

    return () => {
      console.log('⏰ Clearing periodic forms refresh');
      clearInterval(pollInterval);
    };
  }, []);

  const fetchIntakeForms = async () => {
    try {
      setLoading(true);
      const data = await intakeFormService.getIntakeForms();
      setIntakeForms(data || []);
    } catch (error) {
      console.error('Error fetching intake forms:', error);
      setError('Failed to load intake forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await patientService.getPatients();
      const patientsMap = {};

      if (response && Array.isArray(response)) {
        response.forEach(patient => {
          patientsMap[patient._id || patient.id] = patient;
        });
      } else if (response && response.data) {
        response.data.forEach(patient => {
          patientsMap[patient._id || patient.id] = patient;
        });
      }

      setPatients(patientsMap);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleDeleteIntakeForm = async (id) => {
    if (window.confirm('Are you sure you want to delete this intake form?')) {
      try {
        await intakeFormService.deleteIntakeForm(id);
        await fetchIntakeForms();
      } catch (error) {
        console.error('Error deleting intake form:', error);
        alert('Failed to delete intake form');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'complete':
        return '#4CAF50';
      case 'pending':
        return '#FF9800';
      case 'draft':
        return '#FFC107';
      case 'archived':
        return '#9E9E9E';
      default:
        return '#2196F3';
    }
  };

  const filteredIntakeForms = intakeForms.filter(form => {
    const matchesSearch = form.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          patients[form.patientId]?.fullName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || form.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Layout>
        <div className="intake-forms-loading">
          <div className="loading-spinner"></div>
          <p>Loading intake forms...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="intake-forms-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchIntakeForms} className="btn-retry">
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="intake-forms-container">
        <div className="intake-forms-header">
          <div>
            <h1>Intake Forms</h1>
            <p className="subtitle">Manage patient intake forms and assessments</p>
          </div>
          <button
            onClick={() => navigate('/intake-form')}
            className="btn-create-intake"
          >
            <span>+</span> New Intake Form
          </button>
        </div>

        <div className="intake-forms-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by patient name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="status-filter">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {filteredIntakeForms.length === 0 ? (
          <div className="no-intake-forms">
            <div className="empty-state">
              <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
              <h3>No Intake Forms Found</h3>
              <p>Start by creating your first intake form</p>
              <button
                onClick={() => navigate('/intake-form')}
                className="btn-create-first"
              >
                Create Intake Form
              </button>
            </div>
          </div>
        ) : (
          <div className="intake-forms-table">
            <table>
              <thead>
                <tr>
                  <th>Patient Name</th>
                  <th>Date Created</th>
                  <th>Status</th>
                  <th>Chief Complaint</th>
                  <th>Domain</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIntakeForms.map((form) => (
                  <tr key={form._id}>
                    <td>
                      <div className="patient-info">
                        <strong>{form.fullName || patients[form.patientId]?.fullName || 'N/A'}</strong>
                        {form.email && (
                          <span className="patient-email">{form.email}</span>
                        )}
                      </div>
                    </td>
                    <td>{formatDate(form.createdAt)}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(form.status) }}
                      >
                        {form.status || 'Draft'}
                      </span>
                    </td>
                    <td className="chief-complaint">
                      {form.chiefComplaint || 'N/A'}
                    </td>
                    <td>
                      {form.selectedDomain?.name || 'General'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => navigate(`/intake-form/view/${form._id}`)}
                          className="btn-action btn-view"
                          title="View"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => navigate(`/intake-form/edit/${form._id}`)}
                          className="btn-action btn-edit"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteIntakeForm(form._id)}
                          className="btn-action btn-delete"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default IntakeForms;